import { injectStrict } from '@/injection/injectStrict.ts';
import { ApiKey } from '@/injection/keys.ts';
import { ApiRequestParams } from '@/types/api.type.ts';
import {
    Auction,
    AuctionBid,
    AuctionBidCreateForm,
    AuctionBidCreateFormErrors,
    AuctionBidsSchema,
    AuctionCarMonthCounter,
    AuctionCreateForm,
    AuctionCreateFormErrors,
    AuctionCreateFormErrorsSchema,
    AuctionsCarsMonthCountersSchema,
    AuctionSchema,
    AuctionSetDeliveryRequiredForm,
    AuctionSetDeliveryRequiredFormErrors,
    AuctionSetDeliveryRequiredFormErrorsSchema,
    AuctionsPaginator,
    AuctionsPaginatorSchema,
    AuctionUpdateForm,
    AuctionUpdateFormErrors,
    AuctionUpdateFormErrorsSchema,
    CarLastAuctionListItem,
    CarLastAuctionListSchema,
} from '@/types/auction.type.ts';
import { convertFlatMapToNestedMap } from '@/utils/collection-helpers.ts';

class AuctionRepository
{
    private $Api: any;

    constructor() {
        this.$Api = injectStrict(ApiKey);
    }

    public getLastAuctionByCarsIds(car_ids: number[]): Promise<CarLastAuctionListItem[]>
    {
        const requestParams: ApiRequestParams = {
            query: { car_ids },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetLastAuctionByCarsIds', requestParams)
                .then(({ data }: any) => {
                    const parsedAuctions = CarLastAuctionListSchema.parse(data);
                    const auctions: CarLastAuctionListItem[] = parsedAuctions as unknown as CarLastAuctionListItem[];

                    resolve(auctions);
                });
        });
    }

    public getAuctions(oQuery?: any): Promise<AuctionsPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: oQuery,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetAuctions', requestParams)
                .then(({ data }: any) => {
                    const parsedAuctionsPaginator = AuctionsPaginatorSchema.parse(data);
                    const auctionsPaginator: AuctionsPaginator = parsedAuctionsPaginator as unknown as AuctionsPaginator;

                    resolve(auctionsPaginator);
                });
        });
    }

    public forceCompleteAuction(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('ForceCompleteAuction', requestParams)
                .then(() => resolve())
                .catch(({ response: { data: { message } } }: any) => reject(message));
        });
    }

    public prolongAuction(id: number, prolong_minutes: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: {
                prolong_minutes,
            },
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('ProlongAuction', requestParams)
                .then(() => resolve())
                .catch(({ response: { data: { message } } }: any) => reject(message));
        });
    }

    public createAuction(form: AuctionCreateForm): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            requestBody: form,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('CreateAuction', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const nestedErrors = convertFlatMapToNestedMap(errors);
                        const parsedErrors = AuctionCreateFormErrorsSchema.parse(nestedErrors);
                        const createErrors: AuctionCreateFormErrors = parsedErrors as unknown as AuctionCreateFormErrors;

                        reject(createErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public getAuction(id: number): Promise<Auction>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetAuction', requestParams)
                .then(({ data }: any) => {
                    const parsedAuction = AuctionSchema.parse(data);
                    const auction: Auction = parsedAuction as unknown as Auction;

                    resolve(auction);
                });
        });
    }

    public updateAuction(id: number, form: AuctionUpdateForm): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: form,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('UpdateAuction', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedErrors = AuctionUpdateFormErrorsSchema.parse(errors);
                        const updateErrors: AuctionUpdateFormErrors = parsedErrors as unknown as AuctionUpdateFormErrors;

                        reject(updateErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public publishAuction(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('PublishAuction', requestParams)
                .then(() => resolve())
                .catch(() => reject());
        });
    }

    public deleteAuction(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('DeleteAuction', requestParams)
                .then(() => resolve());
        });
    }

    public sendAnnouncementAboutAuction(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('SendAnnouncementAboutAuction', requestParams)
                .then(() => resolve());
        });
    }

    public annulAuction(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('AnnulAuction', requestParams)
                .then(() => resolve());
        });
    }

    public createAuctionBid(id: number, form: AuctionBidCreateForm): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: form,

        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('CreateAuctionBid', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedErrors = AuctionBidCreateFormErrors.parse(errors);
                        const createErrors: AuctionBidCreateFormErrors = parsedErrors as unknown as AuctionBidCreateFormErrors;

                        reject(createErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public getAuctionBids(id: number): Promise<AuctionBid[]>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetAuctionBids', requestParams)
                .then(({ data }: any) => {
                    const parsedBids = AuctionBidsSchema.parse(data);
                    const bids: AuctionBid[] = parsedBids as unknown as AuctionBid[];

                    resolve(bids);
                });
        });
    }

    public confirmAuctionWinner(id: number): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('ConfirmAuctionWinner', requestParams)
                .then(() => resolve());
        });
    }

    public setAuctionDeliveryRequired(id: number, form: AuctionSetDeliveryRequiredForm): Promise<void>
    {
        const requestParams: ApiRequestParams = {
            segments: { id },
            requestBody: form,
        };

        return new Promise((resolve, reject) => {
            this.$Api.callNew('SetAuctionDeliveryRequired', requestParams)
                .then(() => resolve())
                .catch(({ response: { status, data: { errors } } }: any) => {
                    if (status === 422) {
                        const parsedErrors = AuctionSetDeliveryRequiredFormErrorsSchema.parse(errors);
                        const formErrors: AuctionSetDeliveryRequiredFormErrors = parsedErrors as unknown as AuctionSetDeliveryRequiredFormErrors;

                        reject(formErrors);
                    }
                    else {
                        reject();
                    }
                });
        });
    }

    public getJoiningAuctions(oQuery?: any): Promise<AuctionsPaginator>
    {
        const requestParams: ApiRequestParams = {
            query: oQuery,
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetJoiningAuctions', requestParams)
                .then(({ data }: any) => {
                    const parsedAuctionsPaginator = AuctionsPaginatorSchema.parse(data);
                    const auctionsPaginator: AuctionsPaginator = parsedAuctionsPaginator as unknown as AuctionsPaginator;

                    resolve(auctionsPaginator);
                });
        });
    }

    public getAuctionCountByCarIdOnCurrentMonth(car_ids: number[]): Promise<AuctionCarMonthCounter[]>
    {
        const requestParams: ApiRequestParams = {
            query: { car_ids },
        };

        return new Promise((resolve) => {
            this.$Api.callNew('GetAuctionCountByCarIdOnCurrentMonth', requestParams)
                .then(({ data }: any) => {
                    const parsedCounters = AuctionsCarsMonthCountersSchema.parse(data);
                    const counters: AuctionCarMonthCounter[] = parsedCounters as unknown as AuctionCarMonthCounter[];

                    resolve(counters);
                });
        });
    }
}

export default AuctionRepository;
